/**
 * Propostas comerciais.
 *
 * A lista responde a pergunta que o comercial faz toda segunda-feira: o que foi enviado e ainda
 * não voltou. Por isso a situação vem antes do valor, e a ordem é a de criação — a proposta de
 * ontem é a que ainda está em jogo.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Chip, Alert, Button } from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Print as PrintIcon,
} from '@mui/icons-material';
import { PageShell, DataTable, type DataTableColumn } from '../../components';
import proposalService from '../../services/proposalService';
import {
  emReais,
  ROTULO_DA_SITUACAO,
  type Proposta,
  type SituacaoDaProposta,
} from '../../types/proposal';
import { Tooltip } from '@mui/material';

function corDaSituacao(
  situacao: SituacaoDaProposta
): 'default' | 'info' | 'success' | 'error' {
  if (situacao === 'ENVIADA') return 'info';
  if (situacao === 'ACEITA') return 'success';
  if (situacao === 'RECUSADA') return 'error';
  return 'default';
}

/** Data e hora curtas — na lista o que interessa é "quando", não os segundos. */
function quandoBR(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

function dataBR(iso: string | null | undefined): string {
  if (!iso) return '—';
  const [ano, mes, dia] = iso.slice(0, 10).split('-');
  return `${dia}/${mes}/${ano}`;
}

/** Quem emitiu, como se lê: a plataforma não tem prestador, e o nome dela é o do produto. */
function emissorDe(p: Proposta): string {
  return p.provider?.tradeName ?? p.provider?.legalName ?? 'Lidera SST';
}

const COLUNAS_BASE: DataTableColumn<Proposta>[] = [
  { id: 'number', label: 'Número', primary: true, render: (p) => p.number },
  { id: 'clientName', label: 'Cliente', render: (p) => p.clientName },
  { id: 'lives', label: 'Vidas', align: 'right', render: (p) => String(p.lives) },
  {
    id: 'total',
    label: 'Mensal',
    align: 'right',
    render: (p) => emReais(p.totais.total),
  },
  {
    id: 'clube',
    label: 'Clube',
    render: (p) =>
      p.clubEnabled ? (
        <Chip size="small" label="Incluso" color="secondary" variant="outlined" />
      ) : (
        '—'
      ),
  },
  {
    /**
     * A coluna que muda a ligação de segunda-feira: "ele nunca abriu" e "ele abriu três vezes"
     * pedem conversas diferentes. Por isso a contagem aparece aqui, e não só no documento.
     */
    id: 'visualizacao',
    label: 'Cliente abriu',
    render: (p) => {
      if (p.viewCount > 0) {
        return (
          <Tooltip title={`Última vez em ${quandoBR(p.lastViewedAt)}`}>
            <Chip
              size="small"
              color="success"
              variant="outlined"
              label={p.viewCount === 1 ? '1 vez' : `${p.viewCount} vezes`}
            />
          </Tooltip>
        );
      }
      if (p.shareToken) {
        return <Chip size="small" variant="outlined" label="Link enviado" />;
      }
      return '—';
    },
  },
  {
    id: 'status',
    label: 'Situação',
    render: (p) => (
      <Chip
        size="small"
        label={ROTULO_DA_SITUACAO[p.status] ?? p.status}
        color={corDaSituacao(p.status)}
      />
    ),
  },
  { id: 'createdAt', label: 'Criada em', render: (p) => dataBR(p.createdAt) },
];

export const ProposalListPage: React.FC = () => {
  const navegar = useNavigate();
  const [propostas, setPropostas] = useState<Proposta[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      setPropostas(await proposalService.listar());
    } catch (err: unknown) {
      setErro(err instanceof Error ? err.message : 'Não foi possível carregar as propostas');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const emAberto = useMemo(
    () => propostas.filter((p) => p.status === 'ENVIADA').length,
    [propostas]
  );

  /**
   * A coluna "Emitida por" só existe quando há mais de um emissor na lista.
   *
   * A numeração é por emissor, então a proposta da clínica e a da própria casa podem ser as duas
   * `2026-001` — e na Plataforma, que enxerga as duas, ficariam indistinguíveis. Para quem opera
   * dentro de uma clínica a coluna diria a mesma coisa em toda linha, e aí ela só ocuparia
   * espaço.
   */
  const colunas = useMemo(() => {
    const emissores = new Set(propostas.map(emissorDe));
    if (emissores.size <= 1) return COLUNAS_BASE;

    const comEmissor = [...COLUNAS_BASE];
    comEmissor.splice(1, 0, {
      id: 'emissor',
      label: 'Emitida por',
      render: emissorDe,
    });
    return comEmissor;
  }, [propostas]);

  const remover = useCallback(
    async (p: Proposta) => {
      try {
        await proposalService.remover(p.id);
        await carregar();
      } catch (err: unknown) {
        setErro(err instanceof Error ? err.message : 'Não foi possível excluir a proposta');
      }
    },
    [carregar]
  );

  return (
    <PageShell
      breadcrumbs={[{ label: 'Início', path: '/' }, { label: 'Propostas' }]}
      title="Propostas Comerciais"
      subtitle="Gestão em SST por vida e Clube Lidera — o cálculo e o documento saem daqui"
      actions={
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navegar('/proposals/nova')}
        >
          Nova proposta
        </Button>
      }
    >
      {erro && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {erro}
        </Alert>
      )}

      {emAberto > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {emAberto === 1
            ? '1 proposta enviada aguardando resposta.'
            : `${emAberto} propostas enviadas aguardando resposta.`}
        </Alert>
      )}

      <DataTable
        columns={colunas}
        rows={propostas}
        getRowId={(p) => p.id}
        loading={carregando}
        onRowClick={(p) => navegar(`/proposals/${p.id}`)}
        emptyState={{
          title: 'Nenhuma proposta criada ainda',
          description:
            'A proposta calcula o valor mensal a partir do número de vidas, soma o Clube Lidera quando ele entra, e gera o documento pronto para enviar ao cliente.',
        }}
        actions={[
          {
            icon: <PrintIcon fontSize="small" />,
            tooltip: 'Abrir o documento para enviar',
            onClick: (p: Proposta) => navegar(`/proposals/${p.id}/documento`),
          },
          {
            icon: <EditIcon fontSize="small" />,
            tooltip: 'Editar',
            onClick: (p: Proposta) => navegar(`/proposals/${p.id}`),
          },
          {
            icon: <DeleteIcon fontSize="small" />,
            tooltip: 'Excluir',
            color: 'error',
            onClick: (p: Proposta) => void remover(p),
          },
        ]}
      />
    </PageShell>
  );
};

export default ProposalListPage;
